import React, { useEffect, useState } from 'react';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import DateRangePicker from "../../../components/DateRangePicker";
import { formatDate } from "../../../utils/helper";

const List = () => {
    const [isLoader, setIsLoader] = useState(false);
    const [promoCodes, setPromoCodes] = useState([]);
    const [pagination, setPagination] = useState({});
    const [apiData, setApiData] = useState({
        search: "",
        daterange: "",
        page: 1,
        status: "",
    });

    useEffect(() => {
        getPromoCodes();
    }, [apiData.page, apiData.search, apiData.daterange, apiData.status]);

    const getPromoCodes = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/promo-codes",
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                setPromoCodes(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // handled
        } finally {
            setIsLoader(false);
        }
    };

    const handlePageChange = (page) => setApiData(prev => ({ ...prev, page }));

    const clearFilter = () => setApiData({ search: '', daterange: '', page: 1, status: '' });

    const delData = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'You will not be able to revert this!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });
        if (result.isConfirmed) {
            const response = await ApiService.request({ method: "DELETE", url: `admin/promo-codes/${id}` });
            const data = response.data;
            if (data.status) { getPromoCodes(); toast.success(data.message); }
            else toast.error(data.message);
        }
    };

    const updateStatus = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'You want to change its status!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4BC95F',
            confirmButtonText: 'Yes',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });
        if (result.isConfirmed) {
            const response = await ApiService.request({ method: "POST", url: `admin/promo-codes/status/${id}` });
            const data = response.data;
            if (data.status) { getPromoCodes(); toast.success(data.message); }
            else toast.error(data.message);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Promo Codes
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Promo Codes</li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/promo-codes/create" className="btn btn-base btn-sm">Add New Promo Code</Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <div className="row">
                                    <div className="col-md-3">
                                        <input
                                            type="text"
                                            value={apiData.search}
                                            onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })}
                                            className="form-control form-control-solid"
                                            placeholder="Search by code or title..."
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <DateRangePicker
                                            value={apiData.daterange}
                                            onChange={(daterange) => setApiData({ ...apiData, daterange, page: 1 })}
                                            placeholder="Select Date Range"
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <select
                                            className="form-control form-control-solid"
                                            name="status"
                                            value={apiData.status}
                                            onChange={e => setApiData({ ...apiData, status: e.target.value, page: 1 })}
                                        >
                                            <option value="">All Status</option>
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2 mt-2">
                                        <button
                                            type="button"
                                            onClick={clearFilter}
                                            className={`btn btn-sm btn-secondary ${!apiData.search && !apiData.daterange && !apiData.status ? 'd-none' : ''}`}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body pt-0">
                            <div className="table-responsive">
                                <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                    <thead>
                                        <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                            <th>Code</th>
                                            <th>Title</th>
                                            <th>Discount</th>
                                            <th>Min Order</th>
                                            <th>Expiry</th>
                                            <th>User Type</th>
                                            <th>Created Date</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="fw-semibold text-gray-600 fs-6">
                                        {promoCodes.length > 0 ? promoCodes.map((promo, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <span className="badge badge-light-primary fs-7">{promo.code}</span>
                                                </td>
                                                <td>{promo.title}</td>
                                                <td>
                                                    {promo.discount_type === 'percentage'
                                                        ? `${promo.discount}%`
                                                        : `${promo.discount} (fixed)`}
                                                </td>
                                                <td>{promo.min_order || '-'}</td>
                                                <td>
                                                    {promo.expiry_type === 'date'
                                                        ? (promo.expiry_date ? formatDate(promo.expiry_date, 'DD MMM YYYY') : '-')
                                                        : (promo.expiry_count ? `${promo.expiry_count} uses` : '-')}
                                                </td>
                                                <td className="text-capitalize">{promo.user_type}</td>
                                                <td>{formatDate(promo.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                                                <td>
                                                    <button className="p-0 btn btn-transparent" onClick={() => updateStatus(promo._id)}>
                                                        <span className={`badge ${promo.status ? 'badge-light-success' : 'badge-light-danger'}`}>
                                                            {promo.status ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </button>
                                                </td>
                                                <td>
                                                    <Link to={`/promo-codes/create/${promo._id}`} className="btn btn-sm h-35px btn-light-primary me-1">
                                                        <i className="bi bi-pencil-fill fs-6"></i>
                                                    </Link>
                                                    <button onClick={() => delData(promo._id)} className="btn btn-sm h-35px btn-light-primary">
                                                        <i className="bi bi-trash-fill fs-6"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="9" className="text-dark text-center mt-2">No data found!</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                {isLoader && <Loader text="Loading" size="30" className="text-center" />}
                                <span>Total Records: {pagination?.total_records}</span>
                                {pagination.total_pages > 1 && (
                                    <Paginator
                                        page={pagination.current_page}
                                        rows={pagination.per_page}
                                        totalRecords={pagination.total_records}
                                        onPageChange={handlePageChange}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
